import { supabase } from '../lib/supabase';

export const emailReceiptService = {
  /**
   * Send POS transaction receipt email
   */
  async sendPOSReceipt({
    transactionId,
    transactionNumber,
    recipientEmail,
    recipientName,
    items,
    subtotal,
    taxAmount,
    discountAmount,
    totalAmount,
    paymentMethod,
    transactionDate,
    tenantName,
    cashierName,
    tenantId
  }) {
    try {
      // Create email receipt record
      const { data: receiptRecord, error: receiptError } = await supabase
        ?.from('email_receipts')
        ?.insert([{
          tenant_id: tenantId,
          transaction_type: 'pos_sale',
          transaction_id: transactionId,
          transaction_number: transactionNumber,
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          subject: `Receipt for ${transactionNumber} - ${tenantName}`,
          email_status: 'pending',
          email_content: {
            items,
            subtotal,
            taxAmount,
            discountAmount,
            totalAmount,
            paymentMethod,
            transactionDate
          }
        }])
        ?.select()
        ?.single();

      if (receiptError) throw receiptError;

      // Send email via Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-pos-receipt', {
        body: {
          recipientEmail,
          recipientName,
          transactionNumber,
          transactionDate,
          items,
          subtotal,
          taxAmount,
          discountAmount,
          totalAmount,
          paymentMethod,
          tenantName,
          cashierName
        }
      });

      if (error) throw error;

      // Update email receipt record with success
      await supabase
        ?.from('email_receipts')
        ?.update({
          email_status: 'sent',
          resend_message_id: data?.messageId,
          sent_at: new Date()?.toISOString()
        })
        ?.eq('id', receiptRecord?.id);

      return { success: true, data, receiptId: receiptRecord?.id };
    } catch (error) {
      console.error('Error sending POS receipt:', error);
      
      // Update email receipt record with failure
      if (receiptRecord?.id) {
        await supabase
          ?.from('email_receipts')
          ?.update({
            email_status: 'failed',
            failed_reason: error?.message,
            retry_count: receiptRecord?.retry_count + 1
          })
          ?.eq('id', receiptRecord?.id);
      }
      
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send membership payment confirmation email
   */
  async sendMembershipPaymentConfirmation({
    billingRecordId,
    invoiceNumber,
    recipientEmail,
    recipientName,
    planName,
    billingCycle,
    amount,
    taxAmount,
    totalAmount,
    paymentMethod,
    paymentDate,
    nextBillingDate,
    tenantName,
    tenantId
  }) {
    try {
      // Create email receipt record
      const { data: receiptRecord, error: receiptError } = await supabase
        ?.from('email_receipts')
        ?.insert([{
          tenant_id: tenantId,
          transaction_type: 'membership_payment',
          transaction_id: billingRecordId,
          transaction_number: invoiceNumber,
          recipient_email: recipientEmail,
          recipient_name: recipientName,
          subject: `Payment Confirmation - ${planName} Membership`,
          email_status: 'pending',
          email_content: {
            planName,
            billingCycle,
            amount,
            taxAmount,
            totalAmount,
            paymentMethod,
            paymentDate,
            nextBillingDate
          }
        }])
        ?.select()
        ?.single();

      if (receiptError) throw receiptError;

      // Send email via Edge Function
      const { data, error } = await supabase?.functions?.invoke('send-membership-payment-confirmation', {
        body: {
          recipientEmail,
          recipientName,
          invoiceNumber,
          paymentDate,
          planName,
          billingCycle,
          amount,
          taxAmount,
          totalAmount,
          paymentMethod,
          nextBillingDate,
          tenantName
        }
      });

      if (error) throw error;

      // Update email receipt record with success
      await supabase
        ?.from('email_receipts')
        ?.update({
          email_status: 'sent',
          resend_message_id: data?.messageId,
          sent_at: new Date()?.toISOString()
        })
        ?.eq('id', receiptRecord?.id);

      return { success: true, data, receiptId: receiptRecord?.id };
    } catch (error) {
      console.error('Error sending membership payment confirmation:', error);
      
      // Update email receipt record with failure
      if (receiptRecord?.id) {
        await supabase
          ?.from('email_receipts')
          ?.update({
            email_status: 'failed',
            failed_reason: error?.message,
            retry_count: receiptRecord?.retry_count + 1
          })
          ?.eq('id', receiptRecord?.id);
      }
      
      return { success: false, error: error?.message };
    }
  },

  /**
   * Get email receipt history
   */
  async getEmailReceipts(tenantId, filters = {}) {
    try {
      let query = supabase
        ?.from('email_receipts')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.order('created_at', { ascending: false });

      if (filters?.status) query = query?.eq('email_status', filters?.status);
      if (filters?.transactionType) query = query?.eq('transaction_type', filters?.transactionType);
      if (filters?.startDate) query = query?.gte('created_at', filters?.startDate);
      if (filters?.endDate) query = query?.lte('created_at', filters?.endDate);

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching email receipts:', error);
      throw error;
    }
  },

  /**
   * Retry failed email receipt
   */
  async retryEmailReceipt(receiptId) {
    try {
      const { data: receipt, error: fetchError } = await supabase
        ?.from('email_receipts')
        ?.select('*')
        ?.eq('id', receiptId)
        ?.single();

      if (fetchError) throw fetchError;

      if (receipt?.transaction_type === 'pos_sale') {
        return await this.sendPOSReceipt({
          transactionId: receipt?.transaction_id,
          transactionNumber: receipt?.transaction_number,
          recipientEmail: receipt?.recipient_email,
          recipientName: receipt?.recipient_name,
          ...receipt?.email_content,
          tenantId: receipt?.tenant_id
        });
      } else if (receipt?.transaction_type === 'membership_payment') {
        return await this.sendMembershipPaymentConfirmation({
          billingRecordId: receipt?.transaction_id,
          invoiceNumber: receipt?.transaction_number,
          recipientEmail: receipt?.recipient_email,
          recipientName: receipt?.recipient_name,
          ...receipt?.email_content,
          tenantId: receipt?.tenant_id
        });
      }
    } catch (error) {
      console.error('Error retrying email receipt:', error);
      throw error;
    }
  }
};

export default emailReceiptService;